import React from "react"
import { Link } from "gatsby"
import { Container, Column, Columns, Content } from "bloomer"

import Layout from "../components/layout"
import SEO from "../components/seo"
import BreadcrumbJsonLd from "../components/breadcrumb-jsonld"
import OutrosJogos from "../components/outros-jogos"

// Título prolijo para fuentes en MAYÚSCULAS (tarot): "O ÁS DE OUROS" -> "O Ás de Ouros"
const SMALL = new Set(["de", "do", "da", "e", "o", "a", "dos", "das"])
const titleCase = str =>
  str
    .toLowerCase()
    .split(" ")
    .map((w, i) => (i > 0 && SMALL.has(w) ? w : w.charAt(0).toUpperCase() + w.slice(1)))
    .join(" ")

// Meta description: Google muestra ~155 caracteres; cortar en palabra completa.
const clip = (text, max = 155) => {
  const t = String(text || "").replace(/\s+/g, " ").trim()
  if (t.length <= max) return t
  const cut = t.slice(0, max - 1)
  return `${cut.slice(0, cut.lastIndexOf(" "))}…`
}

// Metadatos por colección: hub, imagen, destino de Lua, y cómo armar título/desc/cuerpo.
const CONFIG = {
  "baralho-cigano": {
    hub: "/oraculo/baralho-cigano/",
    hubLabel: "Baralho Cigano",
    luaSigno: "baralho-cigano",
    image: card => require("../data/oraculo/baralho-cigano/images/" + card.id + ".png"),
    label: card => card.title,
    seoTitle: card => `${card.title} no Baralho Cigano: significado da carta | EstrelaGuia`,
    seoDesc: card => `Significado da carta ${card.title} no baralho cigano: ${card.description}`,
    sections: card => [
      { h: "Significado", p: card.description },
      { h: "Interpretação", p: card.interpretation },
    ],
  },
  tarot: {
    hub: "/oraculo/tarot/",
    hubLabel: "Cartas do Tarot",
    luaSigno: "jogo-de-tarot",
    image: card => require("../data/oraculo/jogo-de-tarot/images/" + card.id + "gde.gif"),
    label: card => titleCase(card.title),
    seoTitle: card => `${titleCase(card.title)} no Tarot: significado normal e invertida | EstrelaGuia`,
    seoDesc: card => `${titleCase(card.title)} no tarot: ${card.description}`,
    sections: card => [
      { h: "Significado", p: card.description },
      { h: "Interpretação", p: card.interpretation },
      { h: "Carta invertida", p: card.inverted_interpretation },
    ],
  },
  runas: {
    hub: "/oraculo/runas/",
    hubLabel: "Runas",
    luaSigno: "runas",
    image: card => require("../data/oraculo/runas/images/" + card.name.toLowerCase() + ".png"),
    label: card => card.name,
    seoTitle: card => `Runa ${card.name} (${card.title}): significado | EstrelaGuia`,
    seoDesc: card => `Significado da runa ${card.name}, ${card.title}: ${card.word}. ${card.message}.`,
    sections: card => [
      { h: "Palavra-chave", p: card.word },
      { h: "Mensagem", p: card.message },
      { h: "Advertência", p: card.alert },
      { h: "Significado", p: card.text },
    ],
  },
  cristais: {
    hub: "/oraculo/cristais/",
    hubLabel: "Cristais",
    luaSigno: "cristais",
    image: card => require("../data/oraculo/cristais/images/" + card.id + ".png"),
    label: card => card.name,
    seoTitle: card => `${card.name}: significado e para que serve | EstrelaGuia`,
    seoDesc: card => `${card.name}: ${card.resume}`,
    sections: card => [
      { h: "Para que serve", p: card.resume },
      { h: "Significado", p: card.text },
    ],
  },
}

export default function OraculoCard({ pageContext }) {
  const { collection, card, siblings, index } = pageContext
  const cfg = CONFIG[collection]
  const label = cfg.label(card)
  const image = cfg.image(card)

  // Cartas relacionadas: anterior y siguiente (circular) para interlinking.
  // Ruta real de las páginas de carta (viene de create-card-pages). El hub del tarot es
  // /jogo-de-tarot/ (el juego) pero sus cartas viven en /oraculo/tarot/ — no mezclar.
  const base = `${(pageContext.base || cfg.hub).replace(/\/$/, "")}/`
  const rel = []
  if (siblings.length > 1) {
    const prev = siblings[(index - 1 + siblings.length) % siblings.length]
    const next = siblings[(index + 1) % siblings.length]
    rel.push(prev, next)
  }

  // Sin anuncios hasta que las cartas tengan más texto (política AdSense: contenido fino).
  return (
    <Layout noAds>
      <SEO
        title={cfg.seoTitle(card)}
        description={clip(cfg.seoDesc(card))}
        pathname={`${base}${pageContext.slug}/`}
        image={image}
      />
      <BreadcrumbJsonLd
        trail={[
          { name: "Início", path: "/" },
          { name: cfg.hubLabel, path: cfg.hub },
          { name: label, path: `${base}${pageContext.slug}/` },
        ]}
      />
      <Container>
        <Columns>
          <Column isSize="1/3" className="has-text-centered">
            <img src={image} alt={label} className="oraculo-card-img" />
          </Column>
          <Column>
            <Content>
              <h1>{label}</h1>
              {cfg.sections(card)
                .filter(s => s.p)
                .map(s => (
                  <React.Fragment key={s.h}>
                    <h2>{s.h}</h2>
                    <p>{s.p}</p>
                  </React.Fragment>
                ))}
            </Content>
          <OutrosJogos atual={cfg.hub} />
        </Column>
        </Columns>


        {rel.length > 0 && (
          <Content>
            <h3>Veja também</h3>
            <ul>
              {rel.map(r => (
                <li key={r.slug}>
                  <Link to={`${base}${r.slug}/`}>
                    {collection === "tarot" ? titleCase(r.label) : r.label}
                  </Link>
                </li>
              ))}
              <li>
                <Link to={cfg.hub}>Voltar para {cfg.hubLabel}</Link>
              </li>
            </ul>
          </Content>
        )}
      </Container>
    </Layout>
  )
}
